import { Component } from 'nospa';
import anime from 'animejs';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import mediaBreakpoints from '@/helpers/mediaBreakpoints';
import clickOut from '@/helpers/clickOut';

export default class Header extends Component {
  onInit() {
    this.onClickBurger = this.onClickBurger.bind(this);
    this.onClickNavClose = this.onClickNavClose.bind(this);
    this.onNavClickOut = this.onNavClickOut.bind(this);

    this.onClickSearchButton = this.onClickSearchButton.bind(this);
    this.onClickSearchClose = this.onClickSearchClose.bind(this);
    this.onSearchClickOut = this.onSearchClickOut.bind(this);
    this.onSearchEsc = this.onSearchEsc.bind(this);

    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    this.burgerRef = this.$getRef('burger');
    this.navCloseRef = this.$getRef('navClose');
    this.navRef = this.$getRef('nav');
    this.navOverlayRef = this.$getRef('navOverlay');

    this.searchRef = this.$getRef('search');
    this.searchButtonRef = this.$getRef('searchButton');
    this.searchInputRef = this.$getRef('searchInput');
    this.searchCloseRef = this.$getRef('searchClose');
    this.searchOverlayRef = this.$getRef('searchOverlay');

    this.setEvents();
    this.onBreakpointChange();
    mediaBreakpoints.subscribe(this.onBreakpointChange);
  }

  setEvents(add = true) {
    const action = add ? 'addEventListener' : 'removeEventListener';

    if (this.searchButtonRef) {
      this.searchButtonRef.el[action]('click', this.onClickSearchButton);
    }

    if (this.searchCloseRef) {
      this.searchCloseRef.el[action]('click', this.onClickSearchClose);
    }

    if (this.burgerRef) {
      this.burgerRef.el[action]('click', this.onClickBurger);
    }

    if (this.navCloseRef) {
      this.navCloseRef.el[action]('click', this.onClickNavClose);
    }
  }

  onClickSearchButton(e) {
    e.preventDefault();
    this.showSearch();
  }

  onClickSearchClose(e) {
    e.preventDefault();
    this.hideSearch();
  }

  onSearchClickOut() {
    this.hideSearch();
  }

  onSearchEsc(e) {
    if (e.key === 'Escape') {
      this.hideSearch();
    }
  }

  showSearch(immediate) {
    if (this.isSearchActive) return;
    this.isSearchActive = true;

    clickOut.bind(this.searchRef.el, this.onSearchClickOut);
    disableBodyScroll(this.searchRef.el, {
      reserveScrollBarGap: true,
    });

    const tl = anime.timeline({
      begin: () => {
        if (this.searchRef) {
          this.searchRef.el.style.display = 'block';
        }

        if (this.searchOverlayRef) {
          this.searchOverlayRef.el.style.display = 'block';
        }
      },
      complete: () => {
        if (this.searchInputRef) {
          this.searchInputRef.el.focus();
        }
      },
    });

    if (this.searchRef) {
      anime.remove(this.searchRef);
      tl.add({
        targets: this.searchRef.el,
        translateY: ['-100%', 0],
        easing: 'easeOutCubic',
        duration: immediate ? 0 : 300,
      });
    }

    if (this.searchOverlayRef) {
      anime.remove(this.searchOverlayRef);
      tl.add({
        targets: this.searchOverlayRef.el,
        opacity: 1,
        easing: 'linear',
        duration: immediate ? 0 : 200,
      }, '-=300');
    }

    document.addEventListener('keyup', this.onSearchEsc);
  }

  hideSearch(immediate) {
    if (!this.isSearchActive) return;
    this.isSearchActive = false;

    clickOut.unbind(this.searchRef.el, this.onSearchClickOut);
    enableBodyScroll(this.searchRef.el);

    const tl = anime.timeline({
      complete: () => {
        if (this.paneRef) {
          this.searchRef.el.style.transform = '';
          this.searchRef.el.style.display = '';
        }

        if (this.searchOverlayRef) {
          this.searchOverlayRef.el.style.display = '';
        }
      },
    });

    if (this.searchRef) {
      anime.remove(this.searchRef);
      tl.add({
        targets: this.searchRef.el,
        translateY: '-100%',
        easing: 'easeInCubic',
        duration: immediate ? 0 : 300,
      });
    }

    if (this.searchOverlayRef) {
      anime.remove(this.searchOverlayRef);
      tl.add({
        targets: this.searchOverlayRef.el,
        opacity: 0,
        easing: 'linear',
        duration: immediate ? 0 : 200,
      }, '-=300');
    }

    document.removeEventListener('keyup', this.onSearchEsc);
  }

  onClickBurger(e) {
    e.preventDefault();
    this.openMobileNav();
  }

  onClickNavClose(e) {
    e.preventDefault();
    this.closeMobileNav();
  }

  onNavClickOut() {
    this.closeMobileNav();
  }

  openMobileNav(immediate) {
    if (this.isMobileNavOpened || !this.isMobileNavMode) return;
    this.isMobileNavOpened = true;

    clickOut.bind(this.navRef.el, this.onNavClickOut);
    disableBodyScroll(this.navRef.el, {
      reserveScrollBarGap: true,
    });

    const tl = anime.timeline({
      begin: () => {
        if (this.navRef) {
          this.navRef.el.style.display = 'block';
        }

        if (this.navOverlayRef) {
          this.navOverlayRef.el.style.display = 'block';
        }
      },
    });

    if (this.navRef) {
      anime.remove(this.navRef);
      tl.add({
        targets: this.navRef.el,
        translateX: ['-100%', 0],
        easing: 'easeOutCubic',
        duration: immediate ? 0 : 300,
      });
    }

    if (this.navOverlayRef) {
      anime.remove(this.navOverlayRef);
      tl.add({
        targets: this.navOverlayRef.el,
        opacity: 1,
        easing: 'linear',
        duration: immediate ? 0 : 200,
      }, '-=300');
    }
  }

  closeMobileNav(immediate) {
    if (!this.isMobileNavOpened || !this.isMobileNavMode) return;
    this.isMobileNavOpened = false;

    clickOut.unbind(this.navRef.el, this.onNavClickOut);
    enableBodyScroll(this.navRef.el);

    const tl = anime.timeline({
      complete: () => {
        if (this.navRef) {
          this.navRef.el.style.transform = '';
          this.navRef.el.style.display = '';
        }

        if (this.navOverlayRef) {
          this.navOverlayRef.el.style.display = '';
        }
      },
    });

    if (this.navRef) {
      anime.remove(this.navRef);
      tl.add({
        targets: this.navRef.el,
        translateX: '-100%',
        easing: 'easeInCubic',
        duration: immediate ? 0 : 300,
      });
    }

    if (this.navOverlayRef) {
      anime.remove(this.navOverlayRef);
      tl.add({
        targets: this.navOverlayRef.el,
        opacity: 0,
        easing: 'linear',
        duration: immediate ? 0 : 200,
      }, '-=300');
    }
  }

  onBreakpointChange() {
    const isMobileNavMode = !mediaBreakpoints.isUp(mediaBreakpoints.breakpoints.XL);
    if (this.isMobileNavOpened && !isMobileNavMode) {
      this.closeMobileNav(true);
    }
    this.isMobileNavMode = isMobileNavMode;
  }

  onDestroy() {
    this.setEvents(false);
    this.hideSearch(true);
    this.closeMobileNav(true);
    mediaBreakpoints.unsubscribe(this.onBreakpointChange);
  }
}
